import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Button from '../../shared/components/Button';
import { Input } from '../../shared/components/Input';
import { AddressAutocomplete } from '../../shared/components/AddressAutocomplete';
import {
  useOnboardingTaxonomy,
  useSubmitSelfOnboard,
} from '../../features/onboarding/hooks/useSelfOnboardContract';
import { OnboardingShell } from '../../features/onboarding/components/OnboardingShell';
import { TaxonomyCheckboxGroup } from '../../features/onboarding/components/TaxonomyCheckboxGroup';
import {
  buildSelfOnboardPayload,
  emptySignUpDraft,
  toggleId,
  validateSignUp,
  type SignUpDraft,
} from '../../features/onboarding/signup';

/**
 * PE-2097 — merchant self-onboarding: the sign-up form.
 *
 * Public, no session: `POST /merchants/self-onboard` is `@Public()` on watchman
 * and reached through the osprey-api iam-proxy. Submitting does NOT create a
 * login — watchman emails a single-use contract link, and the account only
 * becomes usable once TCS approves the application. The success state says
 * exactly that, because a "you're all set" message here would be wrong.
 *
 * Optional `?solution=<slug>` on the URL carries the solution a merchant signed
 * up through (`rack-and-box`, `atelier`, …). It is a SLUG, not the UUID the
 * customer-signup DTO wants under the same field name.
 *
 * Next step in the flow is /onboard/contract — see SignContractPage.
 */
export const SignUpPage = () => {
  const [params] = useSearchParams();
  const solution = params.get('solution');

  const { data: taxonomy, isLoading: taxonomyLoading, isError: taxonomyError } = useOnboardingTaxonomy();
  const submit = useSubmitSelfOnboard();

  const [draft, setDraft] = useState<SignUpDraft>(emptySignUpDraft);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const set = (field: keyof SignUpDraft, value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    if (serverError) setServerError('');
  };

  const toggle = (field: 'industryIds' | 'businessCategoryIds', id: string) => {
    setDraft((prev) => ({ ...prev, [field]: toggleId(prev[field], id) }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError('');
    const found = validateSignUp(draft);
    setErrors(found);
    if (Object.keys(found).length) return;

    submit.mutate(buildSelfOnboardPayload(draft, solution), {
      // The applicant has no account yet, so there is nowhere to navigate to —
      // the confirmation replaces the form in place.
      onSuccess: () => setSubmittedEmail(draft.contactEmail.trim()),
      onError: (err: unknown) =>
        setServerError((err as { message?: string })?.message || 'Could not submit your application'),
    });
  };

  if (submittedEmail) {
    return (
      <OnboardingShell>
        <div className="w-full rounded-lg border border-border bg-card p-6 space-y-4 text-center">
          <h1 className="text-2xl font-bold text-foreground">Application received</h1>
          <p className="text-sm text-foreground">
            We&apos;ve emailed <strong>{submittedEmail}</strong> a link to review and sign your
            contract. You need to sign it before your application can be reviewed.
          </p>
          <p className="text-sm text-muted-foreground">
            Your account is not active yet — a member of the TCS team reviews every application, and
            you&apos;ll hear from us once yours is approved.
          </p>
          <Link to="/login" className="inline-block text-sm text-foreground font-medium hover:underline">
            Back to sign in
          </Link>
        </div>
      </OnboardingShell>
    );
  }

  return (
    <OnboardingShell>
      <div className="w-full space-y-2 text-center mb-6">
        <h1 className="text-[28px] font-bold text-foreground">Apply to sell with us</h1>
        <p className="text-sm text-muted-foreground">
          Tell us about your business. You&apos;ll sign your contract next, and we&apos;ll review
          your application before your account goes live.
        </p>
      </div>

      {taxonomyLoading && <p className="text-sm text-muted-foreground">Loading the form…</p>}

      {taxonomyError && (
        <div className="w-full rounded-lg border border-border bg-card p-6 text-center space-y-3">
          <p className="text-sm text-destructive">
            We couldn&apos;t load the industry and category options, so the form can&apos;t be
            completed right now.
          </p>
          <p className="text-sm text-muted-foreground">
            Please try again shortly, or email us and we&apos;ll onboard you directly.
          </p>
        </div>
      )}

      {taxonomy && (
        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <section className="rounded-lg border border-border bg-card p-5 space-y-4">
            <h2 className="font-semibold text-foreground">Your business</h2>
            <div className="space-y-1">
              <Input
                name="businessName"
                label="Business name"
                value={draft.businessName}
                onChange={(e) => set('businessName', e.target.value)}
              />
              {errors.businessName && <p className="text-xs text-destructive">{errors.businessName}</p>}
            </div>
            <div className="space-y-1">
              <label className="text-[14px] font-[600] text-foreground">Company address</label>
              {/* Same Google Places picker the admin merchant/tenant forms use (PE-2272 bug 2).
                  The wrapper falls back to a plain text input when VITE_GOOGLE_MAPS_API_KEY is
                  unset, so this page still works without a key. */}
              <AddressAutocomplete
                value={draft.companyAddress}
                onChange={(val) => set('companyAddress', val)}
                onPlaceSelect={(data) => set('companyAddress', data.formattedAddress)}
                placeholder="Start typing your business address…"
                disabled={submit.isPending}
                error={errors.companyAddress}
              />
            </div>
            <TaxonomyCheckboxGroup
              label="Industry"
              options={taxonomy.industries}
              selected={draft.industryIds}
              onToggle={(id) => toggle('industryIds', id)}
              error={errors.industryIds}
            />
            <TaxonomyCheckboxGroup
              label="Business category"
              options={taxonomy.businessCategories}
              selected={draft.businessCategoryIds}
              onToggle={(id) => toggle('businessCategoryIds', id)}
              error={errors.businessCategoryIds}
            />
          </section>

          <section className="rounded-lg border border-border bg-card p-5 space-y-4">
            <h2 className="font-semibold text-foreground">Contact</h2>
            <div className="space-y-1">
              <Input
                name="contactFullName"
                label="Full name"
                value={draft.contactFullName}
                onChange={(e) => set('contactFullName', e.target.value)}
              />
              {errors.contactFullName && (
                <p className="text-xs text-destructive">{errors.contactFullName}</p>
              )}
            </div>
            <div className="space-y-1">
              <Input
                name="contactEmail"
                label="Email"
                type="email"
                value={draft.contactEmail}
                onChange={(e) => set('contactEmail', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Your contract link and approval decision are sent here.
              </p>
              {errors.contactEmail && <p className="text-xs text-destructive">{errors.contactEmail}</p>}
            </div>
            <Input
              name="contactPhoneNumber"
              label="Phone number (optional)"
              value={draft.contactPhoneNumber}
              onChange={(e) => set('contactPhoneNumber', e.target.value)}
            />
          </section>

          <section className="rounded-lg border border-border bg-card p-5 space-y-4">
            <h2 className="font-semibold text-foreground">Payout account</h2>
            <p className="text-sm text-muted-foreground">
              Where we&apos;ll send your earnings. You can change this later.
            </p>
            <div className="space-y-1">
              <Input
                name="accountName"
                label="Account name"
                value={draft.accountName}
                onChange={(e) => set('accountName', e.target.value)}
              />
              {errors.accountName && <p className="text-xs text-destructive">{errors.accountName}</p>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Input
                  name="bankCode"
                  label="Bank code"
                  value={draft.bankCode}
                  onChange={(e) => set('bankCode', e.target.value)}
                  placeholder="058"
                />
                {/* Free text, not a picker: osprey-api's bank list
                    (GET /payout-destinations/banks) sits behind CollectiveOnlyGuard
                    and is unreachable from this public page. */}
                <p className="text-xs text-muted-foreground">
                  Your bank&apos;s 3-digit NIP code, e.g. 058 for GTBank.
                </p>
                {errors.bankCode && <p className="text-xs text-destructive">{errors.bankCode}</p>}
              </div>
              <div className="space-y-1">
                <Input
                  name="accountNumber"
                  label="Account number"
                  value={draft.accountNumber}
                  onChange={(e) => set('accountNumber', e.target.value)}
                  placeholder="0123456789"
                />
                {errors.accountNumber && (
                  <p className="text-xs text-destructive">{errors.accountNumber}</p>
                )}
              </div>
            </div>
          </section>

          {serverError && <p className="text-sm text-destructive text-center">{serverError}</p>}

          <Button type="submit" loading={submit.isPending} className="w-full">
            Submit application
          </Button>

          <p className="text-sm text-muted-foreground text-center">
            Already have an account?{' '}
            <Link to="/login" className="text-foreground font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </form>
      )}
    </OnboardingShell>
  );
};
